/**
 * GrantCard — Task 26 (ZTNA Customers v2)
 *
 * Renderiza 1 grant (aplicacao autorizada) de um cliente ZTNA com:
 *  - badge tenant/env (monospace, teal),
 *  - display_name + hostname (monospace),
 *  - status de expiracao (vermelho=expirado, laranja<14d, dimmed caso contrario),
 *  - botoes Renovar + Revogar (gated por canRenew/canRevoke).
 *
 * Grant revogado (revoked_at != null) fica opaco (0.5) e esconde acoes.
 *
 * Spec §10.2.
 */
import { Badge, Button, Group, Stack, Text, Tooltip } from '@mantine/core'
import {
  IconCheck,
  IconRefresh,
  IconShieldOff,
  IconTrash,
} from '@tabler/icons-react'
import dayjs from 'dayjs'

import type { CustomerAppGrant } from '@/api/ztnaCustomers'

interface Props {
  grant: CustomerAppGrant
  canRevoke: boolean
  canRenew: boolean
  onRenew: (grant: CustomerAppGrant) => void
  onRevoke: (grant: CustomerAppGrant) => void
  onReactivate?: (grant: CustomerAppGrant) => void
  onDelete?: (grant: CustomerAppGrant) => void
}

export function GrantCard({
  grant,
  canRevoke,
  canRenew,
  onRenew,
  onRevoke,
  onReactivate,
  onDelete,
}: Props) {
  const expDays = dayjs(grant.expires_at).diff(dayjs(), 'day')
  const isExpired = expDays < 0
  const isExpiring = expDays >= 0 && expDays < 14
  const isRevoked = !!grant.revoked_at

  return (
    <Stack
      gap={4}
      p="sm"
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 6,
        opacity: isRevoked ? 0.5 : 1,
      }}
    >
      <Group justify="space-between">
        <Group gap={6}>
          <Badge size="xs" variant="filled" color="teal" ff="monospace">
            {grant.application.tenant_name} / {grant.application.environment}
          </Badge>
          {isRevoked && (
            <Badge size="xs" color="red">
              Revogado
            </Badge>
          )}
        </Group>
      </Group>
      <Text size="sm" fw={600}>
        {grant.application.display_name}
      </Text>
      <Text size="xs" c="dimmed" ff="monospace">
        {grant.application.hostname}
      </Text>
      <Tooltip label={dayjs(grant.expires_at).format('YYYY-MM-DD HH:mm:ss')}>
        <Text
          size="xs"
          c={isExpired ? 'red' : isExpiring ? 'orange' : 'dimmed'}
        >
          Expira: {dayjs(grant.expires_at).format('YYYY-MM-DD')} (
          {isExpired ? 'expirado' : `${expDays}d`})
        </Text>
      </Tooltip>
      {!isRevoked && (canRenew || canRevoke) && (
        <Group gap="xs" mt={4}>
          {canRenew && (
            <Button
              size="xs"
              variant="light"
              color="teal"
              leftSection={<IconRefresh size={12} />}
              onClick={() => onRenew(grant)}
            >
              Renovar
            </Button>
          )}
          {canRevoke && (
            <Button
              size="xs"
              variant="light"
              color="red"
              leftSection={<IconShieldOff size={12} />}
              onClick={() => onRevoke(grant)}
            >
              Revogar acesso
            </Button>
          )}
          {canRevoke && onDelete && (
            <Tooltip label="Remove permanentemente. Diferente de Revogar, NÃO preserva no histórico do cliente.">
              <Button
                size="xs"
                variant="subtle"
                color="red"
                leftSection={<IconTrash size={12} />}
                onClick={() => onDelete(grant)}
              >
                Excluir
              </Button>
            </Tooltip>
          )}
        </Group>
      )}
      {isRevoked && canRevoke && (onReactivate || onDelete) && (
        <Group gap="xs" mt={4}>
          {onReactivate && (
            <Tooltip label="Reativa o acesso a esta aplicação. Se já passou da data de expiração, a validade é estendida por 30 dias.">
              <Button
                size="xs"
                variant="light"
                color="teal"
                leftSection={<IconCheck size={12} />}
                onClick={() => onReactivate(grant)}
              >
                Reativar acesso
              </Button>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip label="Remove permanentemente do cliente. Histórico de auditoria do grant continua existindo.">
              <Button
                size="xs"
                variant="subtle"
                color="red"
                leftSection={<IconTrash size={12} />}
                onClick={() => onDelete(grant)}
              >
                Excluir
              </Button>
            </Tooltip>
          )}
        </Group>
      )}
    </Stack>
  )
}

export default GrantCard
